import type { UseQueryResult } from 'react-query';

import { useCurrentUser } from '@hotjar/user';
import type { OwnedAccount } from '../api/owned-account';

/**
 * This hook returns the optional account that appears on the user object we get from the `/api/v2/users/me` endpoint.
 * This account only exists if the user is the owner of an account and contains additional account details such as
 * billing details that you wouldn't get from reading the account object from the `/api/v1/users/<user_id>/resources`
 * endpoint. If you are not sure that the user may be an owner and you only need basic account information, use the
 * `useCurrentSiteAccount` hook which is always present.
 */
export const useOwnedAccount = (): UseQueryResult<OwnedAccount | undefined, Error> => {
  // We return the first account in the array because for some historical reasons the backend returns an array,
  // but the length can only ever be 1 as a user can only own a single account.
  return useCurrentUser<OwnedAccount | undefined>((currentUser) =>
    currentUser && currentUser.accounts ? currentUser.accounts[0] : undefined,
  );
};
